import { ExperimentName } from '@core/types'
import type { AssetAbManifest } from '@game/types'
import { defineNuxtPlugin, useAssetAbTests, useExperimentVersions, useRequestEvent, useState } from '#imports'
import { ASSET_AB_STATE_KEY } from '../utils/assetAbVariant'

/**
 * Loads the TileGen A/B manifest once per server render into shared state; the
 * client hydrates it from the payload and never refetches. Frozen per page-load
 * on purpose: every tile URL and tracking row of one page-view must describe the
 * same test set, and the per-request query cache cannot promise that.
 *
 * Brands that have not registered `GAME_ASSET_AB` never fetch — the state stays
 * unset and `useAssetAbTests` is inert.
 */
export function loadAssetAbManifest(isServer = import.meta.server): Promise<void> | undefined {
    if (!useExperimentVersions(ExperimentName.GAME_ASSET_AB)) return

    const manifest = useState<AssetAbManifest | null>(ASSET_AB_STATE_KEY, () => null)

    // Warm the per-app memo here, in Nuxt context, so the tracking utils can read
    // the bucket off the app from click handlers without calling a composable.
    useAssetAbTests()

    if (!isServer || manifest.value !== null) return

    return (useRequestEvent()?.context.assetAbManifest() ?? Promise.resolve(null))
        .then((loaded) => {
            manifest.value = loaded
        })
        .catch(() => {
            // Fail-open: no manifest means no tests, the manifest load already logged the cause.
            manifest.value = null
        })
}

export default defineNuxtPlugin({
    name: 'game:asset-ab',
    parallel: true,
    setup: () => loadAssetAbManifest(),
})
